import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { Box } from '@mui/material';
import { OpenCorrection, qualityCheckServices } from '../../../services/qualityCheck';
import { qcErrorMessage } from '../../../services/qcApiError';
import { ROUTE_401 } from '../../../routes/urls';
import { buildVisitWithoutImagesLink } from '../AnalyticsBeta/buildVisitWithoutImagesLink';
import { QcRequestState } from '../QualityCheck/qcRequestState';
import CorrectionsPanel, { CorrectionsState, shortVisitId } from './CorrectionsPanel';
import MarkCorrectedDialog from './MarkCorrectedDialog';
import { canOpenCorrections, seesStudyWideCorrections } from './correctionAccess';

/**
 * SKIN-600 — the "My corrections" page: reads the open flags, opens a flagged visit's patient
 * record, and records "Mark corrected" after the confirmation. A dermatologist's web session
 * lands here (Dashboard); a SUPER_ADMIN reaches it from the menu, study-wide.
 */
export function MyCorrections({ localeCode }: { localeCode: string }) {
    const investigation = useSelector((state: any) => state.investigations.currentInvestigation);
    const history = useHistory();
    const uuidInvestigation: string | undefined = investigation?.uuid;
    const allowed = canOpenCorrections(investigation);
    const studyWide = seesStudyWideCorrections(investigation);

    const [version, setVersion] = useState(0);
    const [state, setState] = useState<CorrectionsState>({ kind: 'loading' });
    const [assigneeFilter, setAssigneeFilter] = useState<string | null>(null);
    const [marking, setMarking] = useState<OpenCorrection | null>(null);
    const [request, setRequest] = useState<QcRequestState>({ status: 'editable' });

    useEffect(() => {
        if (!allowed || !uuidInvestigation) return undefined;
        let cancelled = false;
        setState({ kind: 'loading' });
        qualityCheckServices.listOpenCorrections(uuidInvestigation).then(
            ({ flags, visitReasonSections }) => {
                if (!cancelled) setState({ kind: 'ready', corrections: flags, reasonSections: visitReasonSections });
            },
            (err: unknown) => { if (!cancelled) setState({ kind: 'error', message: qcErrorMessage(err, null) }); },
        );
        return () => { cancelled = true; };
    }, [allowed, uuidInvestigation, version]);

    const openPatientRecord = useCallback((correction: OpenCorrection) => {
        history.push(buildVisitWithoutImagesLink({
            patientInvestigationUuid: correction.patientInvestigationUuid,
            visitUuid: correction.visitUuid,
        }));
    }, [history]);

    const closeDialog = useCallback(() => {
        setMarking(null);
        setRequest({ status: 'editable' });
    }, []);

    const confirmCorrected = useCallback(async (note: string) => {
        if (!marking || !uuidInvestigation) return;
        setRequest({ status: 'submitting' });
        const trimmed = note.trim();
        try {
            // The note is optional; a blank one is not sent.
            await qualityCheckServices.applyCorrectionFlagStep(uuidInvestigation, marking.visitUuid, 'CORRECTED', trimmed ? { note: trimmed } : {});
        } catch (err: unknown) {
            setRequest({ status: 'failed', error: qcErrorMessage(err, null) });
            return;
        }
        closeDialog();
        // The list is the server's: re-read it rather than move the row by hand.
        setVersion((count) => count + 1);
    }, [marking, uuidInvestigation, closeDialog]);

    if (!investigation) return null;
    if (!allowed) return <Redirect to={ROUTE_401} />;

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <CorrectionsPanel
                state={state}
                studyWide={studyWide}
                localeCode={localeCode}
                assigneeFilter={assigneeFilter}
                onAssigneeFilterChange={setAssigneeFilter}
                onOpenPatientRecord={openPatientRecord}
                onMarkCorrected={setMarking}
                onRetry={() => setVersion((count) => count + 1)}
            />
            <MarkCorrectedDialog
                open={marking !== null}
                visitLabel={marking ? shortVisitId(marking.visitUuid) : ''}
                request={request}
                onConfirm={confirmCorrected}
                onCancel={closeDialog}
            />
        </Box>
    );
}

/** `withLocalize` outside the tested component, as `VisitDetail` does, for the same reason. */
const MyCorrectionsLocalized = ({ activeLanguage }: LocalizeContextProps) => (
    <MyCorrections localeCode={activeLanguage.code} />
);

export default withLocalize(MyCorrectionsLocalized);
